import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

export interface ContentLanguage {
  code: string
  name_en: string
  name_native: string
  /** 1 = Safe, 2 = Workable, 3 = Long-tail. Controls display order. */
  tier: 1 | 2 | 3
  active: boolean
  sort_order: number
}

/**
 * Fetches the 97 Gemini-supported content languages — the languages Mori
 * can speak in. Used by the content-language picker on Profile/Settings
 * and onboarding.
 *
 * Ordered by (tier ASC, sort_order ASC): Tier 1 (Safe) appears first,
 * then Tier 2 (Workable), then Tier 3 (Long-tail). Within a tier rows
 * appear in the admin-set order.
 *
 * Pass `activeOnly` to filter out rows with `active = false` (the admin
 * can hide a language without deleting the row).
 */
export function useContentLanguages(opts?: { activeOnly?: boolean }) {
  const query = useQuery({
    queryKey: ['content_languages'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('content_languages')
        .select('code, name_en, name_native, tier, active, sort_order')
        .order('tier')
        .order('sort_order')
      if (error) throw error
      return (data ?? []) as ContentLanguage[]
    },
    staleTime: 30 * 60 * 1000,
  })

  const all = query.data ?? []
  const languages = opts?.activeOnly ? all.filter((l) => l.active) : all

  return {
    languages,
    all,
    isLoading: query.isLoading,
    error: query.error,
  }
}
